import { Link } from "react-router-dom";
import { ChevronDownIcon, LogOutIcon } from "../../../utils/navbar-icons";
import { useNavbar } from "../../../hooks/common/useNavbar";
import { useCurrentGym } from "../../../hooks/gym/useCurrentGym";
import { getResourceUrl } from "../../../utils/images";
import { MobileNavbar } from "./mobileNavbar";
import { Avatar } from "../ui/avatar";
import { useSidebarToggle } from "../../../contexts/sidebar/useSidebarToggle";
import { useThemeToggle } from "../../../hooks/common/useThemeToggle";
import { NotificationBell } from "./NotificationBell";
import { CommandPaletteTrigger } from "../../commandPalette/CommandPaletteTrigger";

export const Navbar = () => {
  const { isLeftCollapsed, isRightCollapsed, toggleLeft, toggleRight } =
    useSidebarToggle();
  const { isDarkMode, toggleTheme } = useThemeToggle();
  const { data: gym } = useCurrentGym();
  const logoSrc = gym?.logoKey ? getResourceUrl(gym.logoKey) : "";
  const {
    gymName,
    email,
    isSuperAdmin,
    superAdminMenuItems,
    menuCategories,
    isMenuOpen,
    openDropdown,
    dropdownRefs,
    toggleDropdown,
    handleMobileMenuToggle,
    closeMobileMenu,
    logout,
    mobileMenuRef,
  } = useNavbar();

  return (
    <>
      <nav className="sticky top-(--height-env-banner) z-55 bg-global-primary border-b border-global-primary shadow-global-card">
        <div className="max-w-full px-3 sm:px-4 navbar-height">
          <div className="hidden lg:flex lg:items-center lg:justify-between h-full gap-2">
            <div className="flex items-center gap-2 xl:gap-4 min-w-0">
              {!isSuperAdmin && (
                <button
                  type="button"
                  onClick={toggleLeft}
                  className="hidden xl:flex items-center justify-center w-7 h-7 rounded-md text-global-tertiary hover:text-global-primary hover:bg-global-overlay-secondary transition-colors"
                  aria-label={
                    isLeftCollapsed ? "Show left panel" : "Hide left panel"
                  }
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-300 ${isLeftCollapsed ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                    />
                  </svg>
                </button>
              )}
              {!isSuperAdmin && (
                <Link
                  to="/"
                  title={gymName || ""}
                  className="flex items-center gap-1.5 min-w-0 max-w-[280px] xl:max-w-[320px] 2xl:max-w-[420px]"
                >
                  <span className="h-7 w-7 block shrink-0">
                    <Avatar
                      src={logoSrc}
                      alt={gymName || ""}
                      fallback={
                        logoSrc
                          ? (gymName?.slice(0, 2).toUpperCase() ?? "")
                          : "Sin imagen"
                      }
                    />
                  </span>
                  <span className="text-global-title font-bold text-size-sm tracking-tight truncate min-w-0">
                    {gymName?.toUpperCase() || ""}
                  </span>
                </Link>
              )}
              {isSuperAdmin && (
                <Link
                  to="/admin/dashboard"
                  className="flex items-center gap-1.5 shrink-0"
                >
                  <span className="h-7 w-7 block shrink-0">
                    <Avatar
                      src={logoSrc}
                      alt="Superadmin"
                      fallback={
                        logoSrc
                          ? "Superadmin".slice(0, 1).toUpperCase()
                          : "Sin imagen"
                      }
                    />
                  </span>
                  <span className="text-global-title font-bold text-size-sm tracking-tight">
                    Superadmin
                  </span>
                </Link>
              )}

              <div className="flex items-center gap-0.5 min-w-0">
                {isSuperAdmin
                  ? superAdminMenuItems.map((item) => {
                      const isOpen = openDropdown === item.id;

                      if (item.hasSubItems) {
                        return (
                          <div
                            key={item.id}
                            className="relative"
                            ref={(el) => {
                              dropdownRefs.current[item.id] = el;
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => toggleDropdown(item.id)}
                              className={`flex items-center gap-1 px-2.5 py-1.5 text-size-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                                isOpen
                                  ? "text-global-title-accent bg-global-accent-overlay"
                                  : "text-global-tertiary hover:text-global-primary hover:bg-global-overlay-secondary"
                              }`}
                            >
                              {item.icon && (
                                <span className="hidden xl:inline-flex">
                                  {item.icon}
                                </span>
                              )}
                              <span>{item.label}</span>
                              <ChevronDownIcon
                                className={`w-3 h-3 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
                              />
                            </button>

                            {isOpen && item.subItems && (
                              <div className="absolute top-full left-0 mt-1 w-48 bg-global-primary border border-global-secondary rounded-md shadow-global-card overflow-hidden z-60">
                                <div className="py-1">
                                  {item.subItems.map((subItem) => (
                                    <Link
                                      key={subItem.href}
                                      to={subItem.href}
                                      onClick={() => toggleDropdown(item.id)}
                                      className="flex items-center gap-2 px-3 py-1.5 text-size-xs text-global-primary hover:bg-global-accent-overlay hover:text-global-title-accent transition-colors"
                                    >
                                      <span className="w-1 h-1 rounded-full bg-global-accent opacity-60" />
                                      <span>{subItem.label}</span>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      return (
                        <Link
                          key={item.id}
                          to={item.href}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-size-xs font-medium rounded-md transition-colors text-global-tertiary hover:text-global-primary hover:bg-global-overlay-secondary"
                        >
                          <span>{item.label}</span>
                        </Link>
                      );
                    })
                  : menuCategories.map((category) => {
                      const isOpen = openDropdown === category.id;
                      const hasVisibleItems = category.items.length > 0;

                      if (!hasVisibleItems) return null;

                      return (
                        <div
                          key={category.id}
                          className="relative"
                          ref={(el) => {
                            dropdownRefs.current[category.id] = el;
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => toggleDropdown(category.id)}
                            className={`flex items-center gap-1 px-2 py-1.5 text-size-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                              isOpen
                                ? "text-global-title-accent bg-global-accent-overlay"
                                : "text-global-tertiary hover:text-global-primary hover:bg-global-overlay-secondary"
                            }`}
                          >
                            <span className="hidden xl:inline-flex">
                              {category.icon}
                            </span>
                            <span>{category.label}</span>
                            <ChevronDownIcon
                              className={`w-3 h-3 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
                            />
                          </button>

                          {isOpen && (
                            <div className="absolute top-full left-0 mt-1 w-48 bg-global-primary border border-global-secondary rounded-md shadow-global-card overflow-hidden z-60">
                              <div className="py-1">
                                {category.items.map((item) => (
                                  <Link
                                    key={item.href}
                                    to={item.href}
                                    onClick={() => toggleDropdown(category.id)}
                                    className="flex items-center gap-2 px-3 py-1.5 text-size-xs text-global-primary hover:bg-global-accent-overlay hover:text-global-title-accent transition-colors"
                                  >
                                    <span className="w-1 h-1 rounded-full bg-global-accent opacity-60" />
                                    <span>{item.label}</span>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <span className="text-size-2xs text-global-tertiary hidden 2xl:inline whitespace-nowrap">
                {new Date().toLocaleDateString("es-ES", {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}
              </span>

              {email && (
                <span className="text-size-2xs text-global-secondary truncate max-w-[150px] hidden 2xl:inline">
                  {email}
                </span>
              )}

              <CommandPaletteTrigger />

              <button
                type="button"
                onClick={toggleTheme}
                className="flex items-center justify-center w-7 h-7 rounded-md text-global-tertiary hover:text-global-primary hover:bg-global-overlay-secondary transition-colors"
                aria-label={
                  isDarkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro"
                }
              >
                {isDarkMode ? (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 7a5 5 0 100 10A5 5 0 0012 7z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                    />
                  </svg>
                )}
              </button>

              <NotificationBell />

              <button
                type="button"
                onClick={() => logout()}
                className="flex items-center gap-1 px-2 py-1 text-size-xs font-medium text-global-tertiary hover:text-global-error hover:bg-global-error/10 rounded-md transition-colors"
              >
                <LogOutIcon className="w-3.5 h-3.5" />
                <span className="hidden xl:inline">Cerrar sesión</span>
              </button>

              {!isSuperAdmin && (
                <button
                  type="button"
                  onClick={toggleRight}
                  className="hidden xl:flex items-center justify-center w-7 h-7 rounded-md text-global-tertiary hover:text-global-primary hover:bg-global-overlay-secondary transition-colors"
                  aria-label={
                    isRightCollapsed ? "Show right panel" : "Hide right panel"
                  }
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-300 ${isRightCollapsed ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 5l7 7-7 7M5 5l7 7-7 7"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <MobileNavbar
            logoSrc={logoSrc}
            gymName={gymName}
            email={email}
            isSuperAdmin={isSuperAdmin}
            isMenuOpen={isMenuOpen}
            openDropdown={openDropdown}
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
            superAdminMenuItems={superAdminMenuItems}
            menuCategories={menuCategories}
            mobileMenuRef={mobileMenuRef}
            dropdownRefs={dropdownRefs}
            toggleDropdown={toggleDropdown}
            handleMobileMenuToggle={handleMobileMenuToggle}
            closeMobileMenu={closeMobileMenu}
            logout={logout}
          />
        </div>
      </nav>
      {isMenuOpen && (
        <div
          className="fixed inset-0 top-[calc(var(--height-navbar-mobile)+var(--height-env-banner))] bg-black/60 z-50 lg:hidden"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
    </>
  );
};
